import styles from './hastaPublicaNovaArrematacao.module.scss';
import { BrButton, BrDivider, BrLoading } from '@govbr-ds/react-components';
import { useContext, useEffect, useState } from 'react';
import { DiefCfemContext } from '../../../../../contexts/DiefCfemContext';
import { mesParaAbreviacao } from '../../../../../utils/mesParaAbreviacao';
import { ICodigoNcm as ICodigoNcmBaseCalculo } from '../../../../../interfaces/IBaseDeCalculo';
import HastaPublicaArrematacaoForm from './components/hastaPublicaArrematacaoForm/hastaPublicaArrematacaoForm';
import { IArrematacao } from '../../../../../interfaces/IArrematacao';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from '../../../lib/backEndRequests';
import { ICodigoNcm } from '../../../../../interfaces/ICodigoNcm';
import ComponentesBaseDeCalculoFormItemNCM from '../../../../../components/baseDeCalculoFormItemNCM/baseDeCalculoFormItemNCM';
import { IMunicipio } from '../../../../../interfaces/IProcessos';
import { useNotificacao } from '../../../../../hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IHastaPublicaNovaArrematacaoProps {
  setNovaArrematacao: React.Dispatch<React.SetStateAction<boolean>>;
  edicaoArrematacao: IArrematacao | undefined;
  setEdicaoArrematacao: React.Dispatch<React.SetStateAction<IArrematacao | undefined>>;
  salvarRascunho: () => void;
}

function HastaPublicaNovaArrematacao({
  setNovaArrematacao,
  edicaoArrematacao,
  setEdicaoArrematacao,
  salvarRascunho,
}: Readonly<IHastaPublicaNovaArrematacaoProps>) {
  const [numeroProcesso, setNumeroProcesso] = useState('');
  const [substancia, setSubstancia] = useState('');
  const [municipio, setMunicipio] = useState<IMunicipio>();
  const [unidadeMedida, setUnidadeMedida] = useState('');

  const [observacoes, setObservacoes] = useState('');

  const [hasProcessoMinerario, setHasProcessoMinerario] = useState(true);

  const [isWithError, setIsWithError] = useState(false);

  const [codigosNCM, setCodigosNCM] = useState<ICodigoNcm[]>([]);

  const [listaCodigosNCM, setListaCodigosNCM] = useState<ICodigoNcmBaseCalculo[]>([]);

  const [isDadoMinerarioSalvo, setIsDadoMinerarioSalvo] = useState(!!edicaoArrematacao);

  const context = useContext(DiefCfemContext);
  const declaracaoHastaPublica = context?.declaracaoHastaPublica;
  const arrematacoes: IArrematacao[] = declaracaoHastaPublica?.arrematacoes || [];

  const mostrarNotificacao = useNotificacao();

  const { getCodigosNCM } = useBackEndRequests();

  const { data: codigosNCMData, isLoading } = useQuery<ICodigoNcm[], IErrorWithSentryId>({
    queryKey: ['codigosNCMQuery'],
    queryFn: () => getCodigosNCM(),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (codigosNCMData) {
      setCodigosNCM(codigosNCMData);
    }
  }, [codigosNCMData]);

  useEffect(() => {
    if (edicaoArrematacao) {
      setHasProcessoMinerario(!!edicaoArrematacao.numeroProcesso);
      setNumeroProcesso(edicaoArrematacao.numeroProcesso || '');
      setSubstancia(edicaoArrematacao.substancia);
      setMunicipio(edicaoArrematacao.municipio);
      setUnidadeMedida(edicaoArrematacao.unidadeMedida);
      setListaCodigosNCM(edicaoArrematacao.componenteBaseDeCalculo.codigosNCM);
      setObservacoes(edicaoArrematacao.observacoes || '');
    }
  }, [edicaoArrematacao]);

  const salvarDadosProcesso = () => {
    if (
      hasProcessoMinerario &&
      (numeroProcesso === '' || isWithError || numeroProcesso.replace(/\D/g, '').length !== 10)
    ) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Você sinalizou que há um processo minerário, preencha-o corretamente',
      });
    } else if (substancia === '' || municipio?.codigoIBGE === '' || unidadeMedida === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Preencha corretamente os campos de substância, município e unidade de medida',
      });
    } else if (
      !!declaracaoHastaPublica?.arrematacoes?.filter(
        (obj) =>
          obj.municipio.codigoIBGE === municipio?.codigoIBGE &&
          obj.numeroProcesso?.toString() === numeroProcesso.replace(/\D/g, '').toString() &&
          obj.substancia.toString() === substancia.toString()
      ).length &&
      edicaoArrematacao === undefined
    ) {
      hasProcessoMinerario
        ? mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Já existe uma arrematação com este número de processo, substância e município.',
          })
        : mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Já existe uma arrematação com esta substância e município sem processo minerário associado.',
          });
    } else {
      setIsDadoMinerarioSalvo(true);
    }
  };

  const concluirAdicao = () => {
    if (!listaCodigosNCM[0]) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Adicione pelo menos um código NCM ao componente de base de cálculo',
      });
    } else {
      const novaArrematacao = {
        numeroProcesso: numeroProcesso.replace(/\D/g, ''),
        substancia: substancia,
        municipio: {
          nome: municipio?.nome || '',
          codigoIBGE: municipio?.codigoIBGE || '',
          uf: municipio?.uf || '',
        },
        unidadeMedida: unidadeMedida,
        observacoes: observacoes.trim(),
        componenteBaseDeCalculo: {
          codigosNCM: listaCodigosNCM,
          quantidadeTotal: listaCodigosNCM.reduce((acumulador, item) => {
            return (parseFloat(acumulador) + parseFloat(item?.quantidade)).toString();
          }, '0'),
          valorTotal: listaCodigosNCM.reduce((acc, item) => acc + parseFloat(item.valor), 0).toString(),
        },
      };

      if (edicaoArrematacao) {
        const index = arrematacoes.findIndex((arrematacao) => arrematacao === edicaoArrematacao);
        arrematacoes[index] = novaArrematacao;
      } else {
        arrematacoes.push(novaArrematacao);
      }

      if (declaracaoHastaPublica) {
        declaracaoHastaPublica.arrematacoes = arrematacoes;
      }

      window.scrollTo(0, 0);
      setEdicaoArrematacao(undefined);
      setNovaArrematacao(false);
      setTimeout(() => salvarRascunho(), 300);
    }
  };

  return (
    <>
      <div className={styles.containerPrincipal}>
        <div className={styles.titleContainer}>
          <h4 className={styles.title}>{edicaoArrematacao ? 'Editar Arrematação' : 'Nova Arrematação'}</h4>
          <span className={styles.mesAno}>
            {mesParaAbreviacao(parseInt(sessionStorage.getItem('mesDeclaracao') || '-1'))}{' '}
            {sessionStorage.getItem('anoDeclaracao')}
          </span>
        </div>
        <div className={styles.formProcessoContainer}>
          <div className={styles.subTitleContainer}>
            <p className={styles.subTitle}>Dados do Processo Minerário</p>
          </div>
          <HastaPublicaArrematacaoForm
            numeroProcesso={numeroProcesso}
            setNumeroProcesso={setNumeroProcesso}
            substancia={substancia}
            setSubstancia={setSubstancia}
            municipio={municipio || { nome: '', codigoIBGE: '', uf: '' }}
            setMunicipio={(value) => setMunicipio(value as IMunicipio)}
            unidadeMedida={unidadeMedida}
            setUnidadeMedida={setUnidadeMedida}
            setIsWithError={setIsWithError}
            edicaoArrematacao={edicaoArrematacao}
            hasProcessoMinerario={hasProcessoMinerario}
            setHasProcessoMinerario={setHasProcessoMinerario}
            isDadoMinerarioSalvo={isDadoMinerarioSalvo}
            observacoes={observacoes}
            setObservacoes={setObservacoes}
          />
          {isDadoMinerarioSalvo && (
            <>
              <div className={styles.dividerContainer}>
                <BrDivider />
              </div>
              <div className={styles.subTitleContainer}>
                <span className={styles.subTitle}>
                  <br />
                  Componentes de Base de Cálculo da CFEM
                </span>
                {isLoading ? (
                  <BrLoading large label='Carregando...' />
                ) : (
                  <div className={styles.componentesBaseCalculoContainer}>
                    <div className={styles.componentesTitle}>Componente de Base de Cálculo Consolidado</div>
                    <ComponentesBaseDeCalculoFormItemNCM
                      listaCodigosNCM={listaCodigosNCM}
                      setListaCodigosNCM={setListaCodigosNCM}
                      codigosNCM={codigosNCM}
                      type='Arrematação'
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles.botaoContainer}>
        <BrButton
          secondary
          onClick={() => {
            window.scrollTo(0, 0);
            setEdicaoArrematacao(undefined);
            setNovaArrematacao(false);
          }}
        >
          Cancelar <i className={`fas fa-times fa-md`} />
        </BrButton>
        <BrButton
          data-testid='salvar-arrematacao'
          primary
          onClick={() => {
            isDadoMinerarioSalvo ? concluirAdicao() : salvarDadosProcesso();
          }}
        >
          Salvar <i className={`fas fa-check fa-md`} />
        </BrButton>
      </div>
    </>
  );
}

export default HastaPublicaNovaArrematacao;
